import { Address, ListVendor } from '../../../models'

/** Volume tier for NVL cards (matches detail ring thresholds). */
export type NVLVendorTier = 'silver' | 'gold' | 'blue' | 'dark_blue' | 'elite'

export interface NVLVendor {
    address: string
    city: string
    complete_count: number
    email: string
    go_back_count: number
    id: number
    name: string
    phone: string
    state: string
    user_count: number
    workorder_count: number
    zip_code: string
    is_trained: boolean
    will_travel: boolean
    plan: string | null
    url: string
    sponsor_image: string
    tagline: string
    featured_image: string
    description: string
    profile_picture: string
    primary_address: Address | null
    is_skillcat: boolean
    distance?: number | null
    /** 0 means no rating. */
    rating?: number
    completion_pct?: number
    go_back_rate?: number
    tier?: NVLVendorTier
    tier_threshold?: number
    tier_progress?: number
}

export const NVL_TIER_LABELS: Record<NVLVendorTier, string> = {
    silver: 'Silver',
    gold: 'Gold',
    blue: 'Platinum',
    dark_blue: 'Diamond',
    elite: 'Elite',
}

export const NVL_TIER_VOLUME_ROWS: Array<{ volume: string; tier: string }> = [
    { volume: '< 1,000', tier: 'Silver' },
    { volume: '< 10,000', tier: 'Gold' },
    { volume: '< 100,000', tier: 'Platinum' },
    { volume: '< 1,000,000', tier: 'Diamond' },
    { volume: '1,000,000+', tier: 'Elite' },
]

export const getVendorTier = (
    workorderCount: number,
): { tier: NVLVendorTier; threshold: number } => {
    if (workorderCount < 1000) {
        return { tier: 'silver', threshold: 1000 }
    }
    if (workorderCount < 10000) {
        return { tier: 'gold', threshold: 10000 }
    }
    if (workorderCount < 100000) {
        return { tier: 'blue', threshold: 100000 }
    }
    if (workorderCount < 1000000) {
        return { tier: 'dark_blue', threshold: 1000000 }
    }
    return { tier: 'elite', threshold: 2000000 }
}

export const enrichNVLVendorMetrics = (vendor: NVLVendor): NVLVendor => {
    const workorderCount = vendor.workorder_count || 0
    const completeCount = vendor.complete_count || 0
    const goBackCount = vendor.go_back_count || 0
    const { tier, threshold } = getVendorTier(workorderCount)

    return {
        ...vendor,
        rating: vendor.rating ?? 0,
        completion_pct:
            vendor.completion_pct ??
            (workorderCount
                ? Math.round((completeCount / workorderCount) * 1000) / 10
                : 0),
        go_back_rate:
            vendor.go_back_rate ??
            (workorderCount
                ? Math.round((goBackCount / workorderCount) * 1000) / 10
                : 0),
        tier: vendor.tier ?? tier,
        tier_threshold: vendor.tier_threshold ?? threshold,
        tier_progress:
            vendor.tier_progress ??
            (threshold
                ? Math.min(
                      Math.round((completeCount / threshold) * 1000) / 10,
                      100,
                  )
                : 0),
    }
}

export type ListMode = 'recommended' | 'nearby' | 'featured' | 'all'

export const LIST_MODE_RECOMMENDED: ListMode = 'recommended'
export const LIST_MODE_NEARBY: ListMode = 'nearby'
export const LIST_MODE_FEATURED: ListMode = 'featured'
export const LIST_MODE_ALL: ListMode = 'all'

export const DEFAULT_NEARBY_RADIUS = 100

export const resolveVendorAddress = (vendor: {
    address?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
    primary_address?: Address | null
    address1?: string | null
}) => {
    const primary = vendor.primary_address
    return {
        address: vendor.address || primary?.address1 || vendor.address1 || '',
        city: vendor.city || primary?.city || '',
        state: vendor.state || primary?.state || '',
        zip_code: vendor.zip_code || primary?.zip_code || '',
        primary_address: primary ?? null,
    }
}

export const normalizeNVLVendor = (
    vendor: NVLVendor & { address1?: string },
): NVLVendor => {
    const addressFields = resolveVendorAddress(vendor)
    return enrichNVLVendorMetrics({
        ...vendor,
        ...addressFields,
        distance:
            vendor.distance === undefined || vendor.distance === null
                ? null
                : Number(vendor.distance),
    })
}

export const mapListVendorToNVL = (vendor: ListVendor): NVLVendor => {
    const recommended = vendor as ListVendor & {
        completed_work_order_count?: number
        is_trained?: boolean
        primary_address?: Address | null
        workorder_count?: number
        go_back_count?: number
        rating?: number
        completion_pct?: number
        go_back_rate?: number
        tier?: NVLVendorTier
        tier_threshold?: number
        tier_progress?: number
        distance?: number | null
    }
    const addressFields = resolveVendorAddress(recommended)
    const completeCount =
        recommended.completed_work_order_count ?? vendor.wo_count ?? 0
    const workorderCount = recommended.workorder_count ?? vendor.wo_count ?? 0

    return enrichNVLVendorMetrics({
        id: vendor.id,
        name: vendor.name,
        phone: vendor.phone ?? '',
        email: vendor.email ?? '',
        ...addressFields,
        is_trained: recommended.is_trained ?? false,
        will_travel: vendor.will_travel ?? false,
        plan: vendor.plan,
        complete_count: completeCount,
        go_back_count: recommended.go_back_count ?? 0,
        user_count: 0,
        workorder_count: workorderCount,
        url: vendor.url ?? '',
        sponsor_image: vendor.sponsor_image ?? '',
        tagline: vendor.tagline ?? '',
        featured_image: vendor.featured_image ?? '',
        description: vendor.description ?? '',
        profile_picture: vendor.profile_picture ?? '',
        is_skillcat: vendor.is_skillcat ?? false,
        distance:
            recommended.distance === undefined || recommended.distance === null
                ? null
                : Number(recommended.distance),
        rating: recommended.rating ?? 0,
        completion_pct: recommended.completion_pct,
        go_back_rate: recommended.go_back_rate,
        tier: recommended.tier,
        tier_threshold: recommended.tier_threshold,
        tier_progress: recommended.tier_progress,
    })
}
